import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// One store-level controller. Prices and inventory remain enforced by Shopify.
class GiftOfferController extends HTMLElement {
  connectedCallback() {
    if (this.connected) return;
    this.connected = true;
    this.listeners = new AbortController();
    this.pending = new Set();
    this.running = false;
    this.queued = false;
    this.blocked = null;
    const initial = JSON.parse(this.querySelector('[data-gift-snapshot]').textContent);
    this.sectionId = initial.sectionId;
    this.config = initial.config;
    this.isEnabled = this.config.enabled === true;
    this.handleEvent = event => {
      if (event.giftOfferSilent) return;
      const pending = Promise.resolve(event.promise).catch(() => {});
      this.pending.add(pending);
      pending.finally(() => {
        this.pending.delete(pending);
        // An unrelated event must not retry the same failed gift indefinitely.
        // Actual customer cart/config changes are detected by the fingerprint.
        this.schedule();
      });
    };
    const options = { signal: this.listeners.signal };
    [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]
      .filter(Boolean).forEach(name => document.addEventListener(name, this.handleEvent, options));
    window.addEventListener('focus', () => this.schedule(), options);
    window.addEventListener('pageshow', event => { if (event.persisted) this.schedule(); }, options);
    this.armDeadline();
    this.schedule();
  }

  disconnectedCallback() {
    this.connected = false;
    this.listeners?.abort();
    clearTimeout(this.timer);
    clearTimeout(this.deadline);
    this.requestController?.abort();
  }

  armDeadline() {
    clearTimeout(this.deadline);
    if (!this.config.enabled) return;
    const dates = [this.config.startsAt, this.config.endsAt].filter(Boolean)
      .map(value => Date.parse(value)).filter(value => Number.isFinite(value) && value > Date.now());
    if (!dates.length) return;
    this.deadline = setTimeout(() => { this.armDeadline(); this.schedule(); },
      Math.min(2147483647, Math.max(50, Math.min(...dates) - Date.now() + 20)));
  }

  schedule() {
    if (!this.connected) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.reconcile(), 100);
  }

  sectionIds() {
    return [...new Set([this.sectionId, ...Array.from(
      document.querySelectorAll('cart-items-component[data-section-id]'),
      element => element.dataset.sectionId
    )])].filter(Boolean).slice(0, 5);
  }

  async request(url, body) {
    this.requestController = new AbortController();
    const timer = setTimeout(() => this.requestController.abort(), 10000);
    try {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin', cache: 'no-store',
        signal: this.requestController.signal,
        // /cart negotiates cart JSON for application/json, ignoring sections.
        // Section Rendering API requests must keep the normal page response.
        headers: { Accept: body ? 'application/json' : '*/*',
          ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      const data = await response.json();
      if (!response.ok || data.errors || data.status >= 400) {
        throw new Error(data.description || data.message || 'Gift update was not accepted.');
      }
      return data;
    } finally {
      clearTimeout(timer);
      this.requestController = null;
    }
  }

  root() { return window.Shopify?.routes?.root || '/'; }

  async snapshot() {
    // Drawer markup is intentionally absent in the cart template.
    // Render in the current page context, preserving variant and preview parameters.
    const url = new URL(location.href);
    url.hash = '';
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', this.sectionIds().join(','));
    const sections = await this.request(url.href);
    const html = sections[this.sectionId];
    if (!html) throw new Error('Gift settings could not be read.');
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const node = documentFragment.querySelector('[data-gift-snapshot]');
    if (!node) throw new Error('Gift state is missing.');
    const data = JSON.parse(node.textContent);
    data.sections = sections;
    if (!Array.isArray(data.cart?.items) || !Number.isFinite(data.cart.total_price)) {
      throw new Error('Gift cart state is invalid.');
    }
    data.cart.currency = data.currency || data.cart.currency;
    this.config = data.config;
    this.isEnabled = this.config.enabled === true;
    this.armDeadline();
    return data;
  }

  evaluate(snapshot) {
    const { config, cart, qualifyingAmount } = snapshot;
    let thresholds;
    try { thresholds = JSON.parse(config.currencyThresholds || '{}'); } catch { thresholds = {}; }
    const currency = cart.currency;
    const major = thresholds && Object.prototype.hasOwnProperty.call(thresholds, currency)
      ? thresholds[currency] : currency === config.baseCurrency ? config.threshold : null;
    const threshold = typeof major === 'number' && Number.isFinite(major) && major > 0
      ? Math.round(major * 100) : null;
    const now = Date.now();
    const start = config.startsAt ? Date.parse(config.startsAt) : -Infinity;
    const end = config.endsAt ? Date.parse(config.endsAt) : Infinity;
    const active = config.enabled === true && config.discountConfirmed === true &&
      config.giftProductId > 0 && config.giftVariantId > 0 && Array.isArray(config.collectionIds) && config.collectionIds.length > 0 &&
      !config.configurationError &&
      threshold !== null && Number.isFinite(qualifyingAmount) &&
      !Number.isNaN(start) && !Number.isNaN(end) && now >= start && now < end;
    const own = cart.items.filter(item => item.properties?._theme_gift_offer === config.offerId);
    const fingerprint = JSON.stringify([
      config.giftProductId, config.giftVariantId, threshold, currency, qualifyingAmount, active,
      cart.items.filter(item => item.properties?._theme_gift_offer !== config.offerId)
        .map(item => [item.product_id, item.variant_id, item.quantity,
          item.original_line_price, item.final_line_price, item.properties || {}])
    ]);
    return {
      snapshot, config, cart, own, threshold, qualifyingAmount,
      active, eligible: active && qualifyingAmount >= threshold,
      fingerprint
    };
  }

  publish(state, error = null) {
    this.state = {
      enabled: state.config.enabled === true, active: state.active,
      eligible: state.eligible, threshold: state.threshold,
      qualifyingAmount: state.qualifyingAmount, currency: state.cart.currency,
      cart: state.cart, giftAdded: state.own.some(item => item.final_line_price === 0), error: error || state.config.configurationError ||
        (state.active && !state.config.giftAvailable ? 'The selected gift variant is currently unavailable.' : null)
    };
    this.dispatchEvent(new CustomEvent('gift-offer:state', { bubbles: true, detail: this.state }));
  }

  async post(endpoint, body) {
    return this.request(`${this.root()}cart/${endpoint}.js`, {
      ...body, sections: this.sectionIds(), sections_url: location.pathname
    });
  }

  async removeGiftLine(line) { await this.post('change', { id: line.key, quantity: 0 }); }

  async perform(state, operation) {
    const deferred = CartLinesUpdateEvent.createPromise();
    // Keep the real native action while explicitly suppressing gift-driven drawer opening.
    const event = new CartLinesUpdateEvent({
      action: operation.action, context: 'cart',
      lines: operation.action === 'add'
        ? [{ merchandiseId: String(state.config.giftVariantId), quantity: 1 }]
        : [{ id: operation.line.key, quantity: operation.quantity }],
      promise: deferred.promise
    });
    event.giftOfferSilent = true;
    this.dispatchEvent(event);
    let error = null;
    let updated = state;
    try {
      if (operation.action === 'add') {
        await this.post('add', { items: [{
          id: state.config.giftVariantId, quantity: 1,
          properties: { _theme_gift_offer: state.config.offerId }
        }] });
      } else if (operation.quantity === 0) {
        await this.removeGiftLine(operation.line);
      } else {
        await this.post('change', { id: operation.line.key, quantity: 1 });
      }
      updated = this.evaluate(await this.snapshot());
      // Do not leave an automatically-added item in the cart at a paid price.
      for (const line of updated.own.filter(item => item.final_line_price !== 0)) {
        await this.removeGiftLine(line);
        error = 'The gift discount did not apply. The automatic gift was removed.';
      }
      if (error) updated = this.evaluate(await this.snapshot());
    } catch (failure) {
      error = failure.message || 'The gift could not be updated.';
      try { updated = this.evaluate(await this.snapshot()); } catch {
        deferred.reject(failure);
        throw failure;
      }
    }
    // A request can time out after Shopify accepted it. Reconcile the actual cart too.
    const paid = updated.own.filter(item => item.final_line_price !== 0);
    if (paid.length) {
      try {
        for (const line of paid) await this.removeGiftLine(line);
        updated = this.evaluate(await this.snapshot());
        error ||= 'The gift discount did not apply. The automatic gift was removed.';
      } catch {
        error = 'The gift could not be removed. Please remove the gift before checkout.';
      }
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated.cart),
      detail: {
        sections: updated.snapshot.sections, items: updated.cart.items,
        itemCount: updated.cart.item_count, source: 'theme-gift-offer', didError: Boolean(error)
      }
    });
    if (error) this.blocked = { fingerprint: updated.fingerprint, message: error };
    return { state: updated, error };
  }

  operation(state) {
    const stale = state.own.find(line => !state.eligible || !state.config.giftAvailable ||
      line.product_id !== state.config.giftProductId || line.variant_id !== state.config.giftVariantId);
    if (stale) return { action: 'remove', line: stale, quantity: 0 };
    if (state.own.length > 1) return { action: 'remove', line: state.own[1], quantity: 0 };
    if (state.own[0]?.quantity !== 1 && state.own.length) {
      return { action: 'update', line: state.own[0], quantity: 1 };
    }
    const unpaid = state.own.find(line => line.final_line_price !== 0);
    if (unpaid) return { action: 'remove', line: unpaid, quantity: 0 };
    const manualGift = state.cart.items.some(line => line.product_id === state.config.giftProductId &&
      line.properties?._theme_gift_offer !== state.config.offerId);
    if (state.eligible && state.config.giftAvailable && !state.own.length && !manualGift &&
        this.blocked?.fingerprint !== state.fingerprint) {
      return { action: 'add', quantity: 1 };
    }
    return null;
  }

  async reconcile() {
    if (!this.connected) return;
    if (this.running) { this.queued = true; return; }
    this.running = true;
    try {
      // Let the theme's pending user action finish before deciding on the gift.
      while (this.pending.size) await Promise.all([...this.pending]);
      if (!this.connected) return;
      let state = this.evaluate(await this.snapshot());
      for (let attempts = 0; attempts < 5; attempts++) {
        const hadPendingAction = this.pending.size > 0;
        while (this.pending.size) await Promise.all([...this.pending]);
        if (!this.connected) return;
        if (hadPendingAction) state = this.evaluate(await this.snapshot());
        const operation = this.operation(state);
        if (!operation) break;
        const result = await this.perform(state, operation);
        state = result.state;
        this.publish(state, result.error);
        if (result.error) break;
      }
      this.publish(state, this.blocked?.fingerprint === state.fingerprint ? this.blocked.message : null);
    } catch (error) {
      this.dispatchEvent(new CustomEvent('gift-offer:error', { bubbles: true,
        detail: { message: error.message || 'Gift state is unavailable.' } }));
    } finally {
      this.running = false;
      if (this.queued && this.connected) { this.queued = false; this.schedule(); }
    }
  }
}

if (!customElements.get('gift-offer-controller')) {
  customElements.define('gift-offer-controller', GiftOfferController);
}
